import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Box, HStack, IconButton, Input, NativeSelect, Spinner, Stack, Switch, Table, Tabs } from "@chakra-ui/react"
import { FaClipboardList } from "react-icons/fa"
import { FiRefreshCw } from "react-icons/fi"
import { LuCalculator } from "react-icons/lu"
import { Button, IntegrityCheckButton, Text, toaster } from "@/components"
import { useViewer } from "@/contexts"
import { addToDocket, checkDealOnDocket } from "@/api"
import { upsertDealClientInfo, upsertDealPunchlist, updateDealClient, getDealManagers, upsertDealOffer, generateContract } from "@/api/deals"
import { getDealFilesForManager } from "@/api/documents"
import { CompanyInfo, DealClient, DealClientInfo as DealClientInfoType, DealPunchlist as DealPunchlistType, DealOffer, EsoDeal } from "@/types"
import { DealClientInfo as DealClientInfoView } from "./DealClientInfo"
import { DealEmailConversation } from "./DealEmailConversation"
import { DealParamsView } from "./DealParamsView"
import { DealPunchlist as DealPunchlistView } from "./DealPunchlist"
import { AddToIrrModal } from "./AddToIrrModal"

interface Props {
  deal: EsoDeal
  company: CompanyInfo
  onRefreshGrants?: () => void
  isRefreshingGrants?: boolean
  offerGeneratorOnly?: boolean
  onSyncClick?: () => void
  isSyncing?: boolean
  onCalcToggle?: () => void
  isCalcOpen?: boolean
}

export const DealTabContent = ({ deal, company, onRefreshGrants, isRefreshingGrants, offerGeneratorOnly, onSyncClick, isSyncing, onCalcToggle, isCalcOpen }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { startImpersonation } = useViewer()
  const [isOnDocket, setIsOnDocket] = useState(false)
  const [isAddingToDocket, setIsAddingToDocket] = useState(false)
  const [isIrrModalOpen, setIsIrrModalOpen] = useState(false)
  const [irrManagers, setIrrManagers] = useState<{ id: number; name: string }[]>([])
  const [isGeneratingContract, setIsGeneratingContract] = useState(false)
  const [contractTestMode, setContractTestMode] = useState(true)

  useEffect(() => {
    checkDealOnDocket(deal.id).then(setIsOnDocket).catch(() => {})
  }, [deal.id])

  useEffect(() => {
    if (deal.dealStage.name === "Closed/Won") {
      getDealManagers().then((mgrs) => setIrrManagers(mgrs)).catch(() => {})
    }
  }, [deal.dealStage.name])

  // Debounced client info save
  const clientInfoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleClientInfoUpdate = useCallback(
    (field: keyof DealClientInfoType, value: string | number | null) => {
      if (clientInfoTimerRef.current) clearTimeout(clientInfoTimerRef.current)
      clientInfoTimerRef.current = setTimeout(async () => {
        try {
          await upsertDealClientInfo(deal.id, { [field]: value })
          queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
        } catch {
          toaster.create({ title: "Failed to save client info", type: "error" })
        }
      }, 500)
    },
    [deal.id, queryClient]
  )

  // Debounced client (name/email/phone) save
  const clientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleClientUpdate = useCallback(
    (field: keyof DealClient, value: string | number | null) => {
      if (clientTimerRef.current) clearTimeout(clientTimerRef.current)
      clientTimerRef.current = setTimeout(async () => {
        try {
          await updateDealClient(deal.id, { [field]: value })
          queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
        } catch {
          toaster.create({ title: "Failed to save client data", type: "error" })
        }
      }, 500)
    },
    [deal.id, queryClient]
  )

  // Debounced punchlist save
  const punchlistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handlePunchlistUpdate = useCallback(
    (field: keyof DealPunchlistType, value: boolean | number | null) => {
      if (punchlistTimerRef.current) clearTimeout(punchlistTimerRef.current)
      punchlistTimerRef.current = setTimeout(async () => {
        try {
          await upsertDealPunchlist(deal.id, { [field]: value })
          queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
        } catch {
          toaster.create({ title: "Failed to save punchlist", type: "error" })
        }
      }, 500)
    },
    [deal.id, queryClient]
  )

  // === Overview tab: offer config state ===
  const currentOfferForConfig = useMemo<DealOffer | null>(() => {
    if (!deal.offers || deal.offers.length === 0) return null
    return deal.offers.find((o) => o.isStaged) || deal.offers[0]
  }, [deal.offers])

  const [configOfferType, setConfigOfferType] = useState(currentOfferForConfig?.offerType || "Traditional")
  const [configAA, setConfigAA] = useState(currentOfferForConfig?.allocationAgreement || "PPA")
  const [configPrefReturn, setConfigPrefReturn] = useState(currentOfferForConfig?.isPreferentialReturn ? "YES" : "NO")
  const [configDesiredLiq, setConfigDesiredLiq] = useState(String(currentOfferForConfig?.desiredLiquidity ?? ""))
  const [configOptionsDocFee, setConfigOptionsDocFee] = useState(String(currentOfferForConfig?.optionsDocFee ?? "0"))
  const [configUpFrontFee, setConfigUpFrontFee] = useState(String(currentOfferForConfig?.upFrontFee ?? "0"))
  const [configExchangeRate, setConfigExchangeRate] = useState(String(currentOfferForConfig?.exchangeRate ?? "1"))

  // Sync config state when offer data updates (after query invalidation)
  useEffect(() => {
    if (!currentOfferForConfig) return
    setConfigOfferType(currentOfferForConfig.offerType || "Traditional")
    setConfigAA(currentOfferForConfig.allocationAgreement || "PPA")
    setConfigPrefReturn(currentOfferForConfig.isPreferentialReturn ? "YES" : "NO")
    setConfigDesiredLiq(String(currentOfferForConfig.desiredLiquidity ?? ""))
    setConfigOptionsDocFee(String(currentOfferForConfig.optionsDocFee ?? "0"))
    setConfigUpFrontFee(String(currentOfferForConfig.upFrontFee ?? "0"))
    setConfigExchangeRate(String(currentOfferForConfig.exchangeRate ?? "1"))
  }, [currentOfferForConfig?.updatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep latest config values in a ref so debounced save always sees fresh data
  const configRef = useRef({
    offerType: configOfferType, aa: configAA, prefReturn: configPrefReturn,
    desiredLiq: configDesiredLiq, optionsDocFee: configOptionsDocFee,
    upFrontFee: configUpFrontFee, exchangeRate: configExchangeRate,
  })
  useEffect(() => {
    configRef.current = {
      offerType: configOfferType, aa: configAA, prefReturn: configPrefReturn,
      desiredLiq: configDesiredLiq, optionsDocFee: configOptionsDocFee,
      upFrontFee: configUpFrontFee, exchangeRate: configExchangeRate,
    }
  }, [configOfferType, configAA, configPrefReturn, configDesiredLiq, configOptionsDocFee, configUpFrontFee, configExchangeRate])

  // Keep current offer in a ref to avoid stale closure in the debounced save
  const currentOfferRef = useRef(currentOfferForConfig)
  useEffect(() => { currentOfferRef.current = currentOfferForConfig }, [currentOfferForConfig])

  const configTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleSaveConfig = useCallback(() => {
    if (configTimerRef.current) clearTimeout(configTimerRef.current)
    configTimerRef.current = setTimeout(async () => {
      const offer = currentOfferRef.current
      const offerNum = offer?.offerNumber ?? 0
      const c = configRef.current
      try {
        await upsertDealOffer(deal.id, offerNum, {
          offerType: c.offerType,
          allocationAgreement: c.aa,
          isPreferentialReturn: c.prefReturn === "YES" ? 1 : 0,
          desiredLiquidity: parseFloat(c.desiredLiq) || null,
          optionsDocFee: parseFloat(c.optionsDocFee) || 0,
          upFrontFee: parseFloat(c.upFrontFee) || 0,
          exchangeRate: parseFloat(c.exchangeRate) || 1,
          // Preserve existing values when offer exists, default to 0 for new offers
          pp: offer?.pp ?? 0,
          ppSplit: offer?.ppSplit ?? 0,
          ltv: offer?.ltv ?? 0,
          hypotheticalSharePrice: offer?.hypotheticalSharePrice ?? 0,
          isIncentive: offer?.isIncentive ?? 0,
          incentivePp: offer?.incentivePp ?? null,
          incentivePpSplit: offer?.incentivePpSplit ?? null,
          incentiveLp: offer?.incentiveLp ?? null,
          incentiveDeadline: offer?.incentiveDeadline ?? null,
        }, [])
        queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
      } catch {
        toaster.create({ title: "Failed to save config", type: "error" })
      }
    }, 800)
  }, [deal.id, queryClient])

  // === Overview tab: client notes ===
  const [notes, setNotes] = useState(deal.clientInfo?.notes || "")
  useEffect(() => {
    setNotes(deal.clientInfo?.notes || "")
  }, [deal.clientInfo?.notes])

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px"
    }
  }, [notes])

  // === Files tab ===
  const [files, setFiles] = useState<Array<{ name: string; date: string; type: string; link: string }>>([])
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [filesError, setFilesError] = useState<string | null>(null)
  const [filesLoaded, setFilesLoaded] = useState(false)

  const handleTabChange = useCallback(async (value: string) => {
    if (value === "files" && !filesLoaded) {
      setIsLoadingFiles(true)
      setFilesError(null)
      try {
        const docs = await getDealFilesForManager(deal.id)
        setFiles(docs)
        setFilesLoaded(true)
      } catch {
        setFilesError("Failed to load files")
      } finally {
        setIsLoadingFiles(false)
      }
    }
  }, [deal.id, filesLoaded])

  const EXCEL_EXTS = [".xlsx", ".xlsm", ".xls", ".xlsb"]
  const isExcelFile = (name: string) => EXCEL_EXTS.some((ext) => name.toLowerCase().endsWith(ext))

  const handleFileClick = (file: { name: string; link: string }) => {
    if (isExcelFile(file.name)) {
      window.open(file.link, "_blank")
    } else {
      window.open(`${process.env.REACT_APP_SERVER_URL}/documents/preview/${deal.id}/${encodeURIComponent(file.name)}`, "_blank")
    }
  }

  const handleAddToDocket = async () => {
    setIsAddingToDocket(true)
    try {
      const { alreadyOnDocket } = await addToDocket(deal.id)
      setIsOnDocket(true)
      queryClient.invalidateQueries({ queryKey: ["docket"] })
      if (alreadyOnDocket) {
        toaster.create({ title: "Already on the docket", type: "info", duration: 3000 })
      } else {
        toaster.create({ title: "Added to docket", type: "success", duration: 3000 })
      }
    } catch {
      toaster.create({ title: "Failed to add to docket", type: "error", duration: 3000 })
    } finally {
      setIsAddingToDocket(false)
    }
  }

  const handleGenerateContract = async () => {
    setIsGeneratingContract(true)
    try {
      const result = await generateContract(deal.id, contractTestMode)
      const fileNames = result.files.map((f: any) => f.name).join(", ")
      toaster.create({
        title: "Contract generated",
        description: result.warnings.length > 0
          ? `${fileNames}\n\nWarnings: ${result.warnings.join(", ")}`
          : fileNames,
        type: "success",
        duration: 8000,
      })
      queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Failed to generate contract"
      toaster.create({ title: msg, type: "error", duration: 5000 })
    } finally {
      setIsGeneratingContract(false)
    }
  }

  const actionButtons = (
    <HStack flexWrap="wrap" gap={2}>
      {onSyncClick && deal.dealIdStr && (
        <IntegrityCheckButton type="deal" dealIdStr={deal.dealIdStr} />
      )}
      {deal.dealStage.name === "Closed/Won" && (
        <Button
          size="sm"
          variant={deal.irrStartDate ? "solid" : "outline"}
          colorPalette={deal.irrStartDate ? "purple" : undefined}
          disabled={!!deal.irrStartDate}
          onClick={() => setIsIrrModalOpen(true)}
        >
          {deal.irrStartDate ? "In IRR" : "Add to IRR"}
        </Button>
      )}
      <HStack gap={1.5} align="center" border="1px solid" borderColor={contractTestMode ? "orange.300" : "border.default"} borderRadius="md" px={2} py={1}>
        <Text fontSize="xs" color={contractTestMode ? "orange.500" : "text.muted"} fontWeight="medium" userSelect="none">
          {contractTestMode ? "Test" : "Live"}
        </Text>
        <Switch.Root
          size="sm"
          colorPalette="orange"
          checked={contractTestMode}
          onCheckedChange={(details) => setContractTestMode(details.checked)}
        >
          <Switch.HiddenInput />
          <Switch.Control />
        </Switch.Root>
        <Button
          size="sm"
          variant="outline"
          onClick={handleGenerateContract}
          loading={isGeneratingContract}
          loadingText="Generating..."
        >
          Generate Contract
        </Button>
      </HStack>
      {onCalcToggle && (
        <IconButton
          aria-label="Toggle calculator"
          size="sm"
          variant={isCalcOpen ? "solid" : "ghost"}
          colorPalette={isCalcOpen ? "blue" : undefined}
          onClick={onCalcToggle}
        >
          <LuCalculator />
        </IconButton>
      )}
      {onSyncClick && (
        <IconButton
          aria-label="Sync deal from legacy database"
          size="sm"
          variant="ghost"
          onClick={onSyncClick}
          disabled={isSyncing}
        >
          {isSyncing ? <Spinner size="xs" /> : <FiRefreshCw />}
        </IconButton>
      )}
    </HStack>
  )

  const irrModal = (
    <AddToIrrModal
      deal={deal}
      managers={irrManagers}
      isOpen={isIrrModalOpen}
      onClose={() => setIsIrrModalOpen(false)}
      onSuccess={() => queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })}
    />
  )

  if (offerGeneratorOnly) {
    return (
      <Tabs.Root
        defaultValue="overview"
        variant="enclosed"
        onValueChange={(details) => handleTabChange(details.value)}
      >
        <Tabs.List>
          <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
          <Tabs.Trigger value="offers">Offers</Tabs.Trigger>
          <Tabs.Trigger value="emails">Emails</Tabs.Trigger>
          <Tabs.Trigger value="files">Files</Tabs.Trigger>
          <Tabs.Trigger value="punchlist">Punchlist</Tabs.Trigger>
        </Tabs.List>
        {irrModal}

        {/* ── Overview ── */}
        <Tabs.Content value="overview">
          <Stack gap={4} my={4}>
            {actionButtons}

            {/* Client Notes */}
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="text.muted" mb={1}>Client Notes</Text>
              <textarea
                ref={textareaRef}
                value={notes}
                placeholder="Add notes..."
                onChange={(e) => {
                  const val = e.target.value
                  setNotes(val)
                  handleClientInfoUpdate("notes", val)
                }}
                style={{
                  width: "100%",
                  resize: "none",
                  overflow: "hidden",
                  minHeight: "56px",
                  border: "1px solid var(--chakra-colors-border-default, #e2e8f0)",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  fontSize: "14px",
                  backgroundColor: "transparent",
                  color: "inherit",
                  fontFamily: "inherit",
                  lineHeight: "1.5",
                  outline: "none",
                }}
              />
            </Box>

            {/* Offer Config */}
              <Box p={4} border="1px solid" borderColor="border.default" borderRadius={8} bg="card.bg">
                <Text fontSize="sm" fontWeight="semibold" mb={3}>
                  {currentOfferForConfig ? `Offer ${currentOfferForConfig.offerNumber} — Configuration` : "New Offer — Configuration"}
                </Text>
                <Table.Root size="sm">
                  <Table.Body>
                    <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                      <Table.Cell fontWeight="semibold" w="180px">Offer Type</Table.Cell>
                      <Table.Cell>
                        <NativeSelect.Root size="sm">
                          <NativeSelect.Field value={configOfferType} onChange={(e) => { setConfigOfferType(e.target.value); scheduleSaveConfig() }}>
                            <option value="Traditional">Traditional</option>
                            <option value="Transfer">Transfer</option>
                            <option value="Forward Contract">Forward Contract</option>
                            <option value="Deemed Sale w/Buy Back">Deemed Sale w/Buy Back</option>
                            <option value="IPO Lockup or Term Loan">IPO Lockup or Term Loan</option>
                            <option value="RSU">RSU</option>
                          </NativeSelect.Field>
                        </NativeSelect.Root>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                      <Table.Cell fontWeight="semibold">Allocation Agreement</Table.Cell>
                      <Table.Cell>
                        <NativeSelect.Root size="sm">
                          <NativeSelect.Field value={configAA} onChange={(e) => { setConfigAA(e.target.value); scheduleSaveConfig() }}>
                            <option value="PPA">PPA</option>
                            <option value="AA">AA</option>
                            <option value="RSU">RSU</option>
                            <option value="IPO">IPO</option>
                            <option value="Transfer">Transfer</option>
                            <option value="Direct">Direct</option>
                            <option value="Forward">Forward</option>
                          </NativeSelect.Field>
                        </NativeSelect.Root>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                      <Table.Cell fontWeight="semibold">Preferential Return</Table.Cell>
                      <Table.Cell>
                        <NativeSelect.Root size="sm">
                          <NativeSelect.Field value={configPrefReturn} onChange={(e) => { setConfigPrefReturn(e.target.value); scheduleSaveConfig() }}>
                            <option value="YES">Yes</option>
                            <option value="NO">No</option>
                          </NativeSelect.Field>
                        </NativeSelect.Root>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                      <Table.Cell fontWeight="semibold">Desired Liquidity</Table.Cell>
                      <Table.Cell>
                        <Input size="sm" variant="flushed" type="number" value={configDesiredLiq} onChange={(e) => { setConfigDesiredLiq(e.target.value); scheduleSaveConfig() }} placeholder="0" />
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                      <Table.Cell fontWeight="semibold">Options Doc Fee</Table.Cell>
                      <Table.Cell>
                        <Input size="sm" variant="flushed" type="number" value={configOptionsDocFee} onChange={(e) => { setConfigOptionsDocFee(e.target.value); scheduleSaveConfig() }} placeholder="0" />
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                      <Table.Cell fontWeight="semibold">Up Front Fee</Table.Cell>
                      <Table.Cell>
                        <Input size="sm" variant="flushed" type="number" value={configUpFrontFee} onChange={(e) => { setConfigUpFrontFee(e.target.value); scheduleSaveConfig() }} placeholder="0" />
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                      <Table.Cell fontWeight="semibold">Exchange Rate</Table.Cell>
                      <Table.Cell>
                        <Input size="sm" variant="flushed" type="number" value={configExchangeRate} onChange={(e) => { setConfigExchangeRate(e.target.value); scheduleSaveConfig() }} placeholder="1" />
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table.Root>
              </Box>
          </Stack>
        </Tabs.Content>

        {/* ── Offers ── */}
        <Tabs.Content value="offers">
          <Stack my={4}>
            <DealParamsView
              deal={deal}
              company={company}
              onRefreshGrants={onRefreshGrants}
              isRefreshingGrants={isRefreshingGrants}
              hideConfigTable
            />
          </Stack>
        </Tabs.Content>

        {/* ── Emails ── */}
        <Tabs.Content value="emails">
          <Stack my={4}>
            <DealEmailConversation
              dealId={deal.id}
              dealManagerEmail={deal.dealManager.email}
              clientEmail={deal.client.email}
              companyId={deal.companyId}
              dealFolderPath={deal.folderPath || undefined}
              companyFolderPath={deal.folderPath ? deal.folderPath.replace(/\/[^/]+\/?$/, "") : undefined}
              companyName={deal.company.name}
            />
          </Stack>
        </Tabs.Content>

        {/* ── Files ── */}
        <Tabs.Content value="files">
          <Stack my={4} gap={2}>
            {isLoadingFiles && (
              <HStack justifyContent="center" py={8} gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="text.muted">Loading files...</Text>
              </HStack>
            )}
            {filesError && (
              <Text fontSize="sm" color="red.500">{filesError}</Text>
            )}
            {!isLoadingFiles && filesLoaded && files.length === 0 && (
              <Text fontSize="sm" color="text.muted" py={6} textAlign="center">No files found in Dropbox</Text>
            )}
            {files.map((file, i) => (
              <HStack
                key={`${file.name}-${i}`}
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="border.default"
                cursor="pointer"
                _hover={{ bg: "hover.bg" }}
                onClick={() => handleFileClick(file)}
                justifyContent="space-between"
                gap={3}
              >
                <HStack gap={3} minW={0} flex={1}>
                  <Box
                    as="span"
                    fontSize="xs"
                    fontWeight="bold"
                    color="gray.500"
                    bg="gray.100"
                    px={2}
                    py="2px"
                    borderRadius="sm"
                    _dark={{ bg: "gray.700", color: "gray.300" }}
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    {file.type || "FILE"}
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" truncate>{file.name}</Text>
                </HStack>
                <Text fontSize="xs" color="text.muted" whiteSpace="nowrap" flexShrink={0}>
                  {file.date ? new Date(file.date).toLocaleDateString() : "—"}
                </Text>
              </HStack>
            ))}
          </Stack>
        </Tabs.Content>

        {/* ── Punchlist ── */}
        <Tabs.Content value="punchlist">
          <Stack my={4}>
            <DealPunchlistView punchlist={deal.punchlist} onUpdate={handlePunchlistUpdate} />
          </Stack>
        </Tabs.Content>
      </Tabs.Root>
    )
  }

  return (
    <Tabs.Root defaultValue="offer-generator" variant="enclosed">
      <HStack>
        <Tabs.List>
          <Tabs.Trigger value="offer-generator">Offer Generator</Tabs.Trigger>
          <Tabs.Trigger value="client-info">Client Info</Tabs.Trigger>
          <Tabs.Trigger value="punchlist">Punchlist</Tabs.Trigger>
        </Tabs.List>
        {actionButtons}
      </HStack>

      {irrModal}

      <Tabs.Content value="offer-generator">
        <Stack my={4}>
          <DealParamsView deal={deal} company={company} onRefreshGrants={onRefreshGrants} isRefreshingGrants={isRefreshingGrants} />
        </Stack>
      </Tabs.Content>

      <Tabs.Content value="client-info">
        <Stack my={4}>
          <DealClientInfoView
            client={deal.client}
            clientInfo={deal.clientInfo}
            onUpdate={handleClientInfoUpdate}
            onClientUpdate={handleClientUpdate}
          />
        </Stack>
      </Tabs.Content>

      <Tabs.Content value="punchlist">
        <Stack my={4}>
          <DealPunchlistView
            punchlist={deal.punchlist}
            onUpdate={handlePunchlistUpdate}
          />
        </Stack>
      </Tabs.Content>
    </Tabs.Root>
  )
}
